import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DesktopMenu, MobileMenu } from './MainNav';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const logoUrl = "/images/logo-eco-vapor-pro-56.webp";

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  // Trap focus within mobile menu when open
  useEffect(() => {
    if (isMenuOpen && menuRef.current) {
      const focusableElements = menuRef.current.querySelectorAll(
        'a, button, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const trapFocus = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      menuRef.current.addEventListener('keydown', trapFocus);
      firstElement?.focus();

      return () => menuRef.current?.removeEventListener('keydown', trapFocus);
    }
  }, [isMenuOpen]);

  return (
    <nav 
      className="fixed top-0 w-full bg-white shadow-sm z-50"
      role="navigation"
      aria-label="Navigazione principale"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link 
            to="/" 
            className="flex items-center gap-2 md:gap-3"
            aria-label="Eco Vapor Pro - Torna alla homepage"
          >
            <img 
              src={logoUrl} 
              alt="Eco Vapor Pro logo" 
              aria-hidden="true"
              className="w-10 h-10 md:w-14 md:h-14 object-contain"
              loading="eager"
              fetchPriority="high"
              width="56"
              height="56"
              decoding="async"
            />
            <span className="text-lg md:text-2xl font-bold text-gray-800">
              Eco <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">Vapor Pro</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-3 ml-auto">
            <DesktopMenu />
            <a href="tel:+393928901442" className="text-sm font-semibold text-gray-800 hover:text-green-600 whitespace-nowrap" data-testid="nav-phone">+39 392 890 1442</a>
            <Link 
              to="/contatti"
              data-testid="nav-cta-preventivo"
              className="bg-gradient-to-r from-green-700 to-blue-700 text-white px-5 py-2.5 rounded-full hover:shadow-xl transition-all transform hover:scale-105 font-semibold text-sm whitespace-nowrap"
              aria-label="Richiedi un preventivo gratuito"
            >
              Preventivo Gratuito
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            ref={menuButtonRef}
            className="lg:hidden p-3 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Chiudi menu di navigazione' : 'Apri menu di navigazione'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-haspopup="true"
            data-testid="mobile-menu-toggle"
          >
            <svg 
              className="w-6 h-6 text-gray-800" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div 
          id="mobile-menu"
          ref={menuRef}
          className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
          role="menu"
          aria-label="Menu di navigazione mobile"
        >
          <div className="container mx-auto px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
            <div className="flex gap-2 mb-3">
              <Link to="/contatti" role="menuitem" data-testid="mobile-cta-preventivo" className="flex-1 text-center bg-gradient-to-r from-green-700 to-blue-700 text-white py-3 rounded-xl font-semibold">Preventivo Gratuito</Link>
              <a href="tel:+393928901442" className="px-4 py-3 rounded-xl border-2 border-green-500 text-green-700 font-semibold" data-testid="mobile-phone">Chiama</a>
            </div>
            <MobileMenu />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
